import React, {
  useEffect,
  useState,
  useImperativeHandle,
  forwardRef,
  useRef,
  useCallback,
  useMemo,
  createContext,
  useContext
} from "react";
import { useHistory } from "react-router-dom";
import Quill from "quill";
import PropTypes from "kf-proptypes/widgets";
import { t } from "@lingui/macro";

import { cloneDeep, MouseWheelListen, debounce } from "base/utils";

import { ErrorBoundary } from "@widgets/common/components/primitives/errorboundary/index.jsx";
import {
  CustomBlock,
  CustomBreak,
  CodeBlockItem,
  BlockQuoteItem,
  Scroll,
  SuggestionList,
  Clipboard,
  Keyboard,
  deserializer,
  deserializeHTML,
  serializer,
  serializeHTML,
  FORMAT_TYPES,
  markdownFromDelta,
  htmlMarkup,
  CUSTOM_TYPES,
  TextWithShiftReturnBlot,
  ASSIST_FORMAT_TYPES,
  isUrl,
  constructSlashCommandList,
  clearPrevLinePlaceholder,
  isCurrentLineEmpty,
  getTagNameByLine,
  getScrollParent
} from "./index.js";
import { InlineToolbar } from "./toolbar/inlinetoolbar.jsx";
import { constructInlineToolbarList } from "./toolbar/inline.util.js";
import {
  SlashCommandRenderer,
  AttachmentTooltip,
  LinkCursorSelectionRenderer,
  PluginRenderer,
  RichtextFilePicker
} from "./components/index.js";

import styles from "./styles/editor.css";

const Delta = Quill.import("delta");

const PROTOCOL_REGEX = /^[a-zA-Z]+:\/\//;

const RichtextContext = createContext();

export const useRichtextContext = () => {
  return useContext(RichtextContext);
};

function RichTextEditorComponent(props) {
  const {
    className = "",
    value = "",
    plugins = [],
    placeholder = t`Start typing and select text or enter '/' for commands`,
    readOnly = false,
    focus,
    onFocus = () => {},
    onEnter,
    searchText = "",
    onChange,
    markdown = false,
    onBlur,
    inlineToolbar = true,
    sideToolbar = false,
    acceptedKeyStrokes,
    shiftReturn = false,
    clearOnEnter = true,
    onClick,
    shouldCheckEmptyonEnter = true,
    innerRef,
    disableTab = false,
    flowType,
    flowId,
    onSelectionChange,
    allowSlashCommand = true,
    fileOptions,
    containerClassname = "",
    onLinkPaste,
    isHTML = false,
    parentContainer,
    copyPasteConfig = {},
    showDefaultFilePicker = true
  } = props;

  const history = useHistory();
  const [currentFocus, setFocus] = useState();
  const [embedBlots, setEmbedBlots] = useState({});
  const [currentReadonly, setReadonly] = useState();
  const [editorState, setEditorState] = useState({
    sideToolbar: {
      top: 0,
      result: "p"
    },
    inlineToolbar: {
      showToolbar: false,
      left: 0,
      right: 0,
      height: 0,
      width: 0
    }
  });
  const [slashCommandTarget, setSlashCommandTarget] = useState({
    left: 0,
    top: 0,
    showTargetRenderer: false,
    height: 0,
    attachmentType: null,
    currentIndex: null
  });
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editorFocussed, setEditorFocussed] = useState(false);
  let editor = useRef(null);
  let currentValue = useRef();
  let formats = [];
  let editorReference = useRef();
  const currentBlots = useRef({});
  const newBlotId = useRef();
  const editorContainer = useRef();
  const pluginRendererRef = useRef(false);
  const [editingLink, setEditingLink] = useState(false);

  /** Register default Modules & formats */
  if (plugins.length > 0) {
    formats.push("custombreak");
    Quill.register(
      {
        "blots/text": TextWithShiftReturnBlot,
        "blots/scroll": Scroll,
        "formats/custombreak": CustomBreak,
        "formats/block_quote/blockquote_item": BlockQuoteItem,
        "formats/code_block/code_block_item": CodeBlockItem,
        "modules/keyboard": Keyboard,
        "modules/clipboard": Clipboard
      },
      true
    );
  }

  /**
   * function to focus the editor
   * @param {boolean} isFocus
   */
  const focusEditor = useCallback(
    function onFocusEditor(isFocus) {
      if (isFocus && !readOnly) {
        setTimeout(() => {
          const length = editor.current.getLength();
          editor.current.setSelection(length);
          editor.current.enable();
          editor.current.scrollIntoView();
        });
      }
    },
    [readOnly]
  );

  /**
   * function to render the custom plugins via portal
   * @param {array} blots
   */
  const onBlotMounted = useCallback(function onBlotMounting(...blots) {
    const embeds = blots.reduce(
      (memo, blot) => {
        memo[blot.id] = blot;
        return memo;
      },
      { ...currentBlots.current }
    );
    newBlotId.current = blots[0].id;
    currentBlots.current = {
      ...currentBlots.current,
      ...embeds
    };
    setEmbedBlots(currentBlots.current);
  }, []);

  /**
   * function to remove the custom plugin from DOM
   * @param {array} unmountedBlot
   */
  const onBlotUnmounted = useCallback(function onBlotUnmounting(unmountedBlot) {
    const { [unmountedBlot.id]: blot, ...otherBlots } = currentBlots.current;
    currentBlots.current = { ...otherBlots };
    setEmbedBlots(currentBlots.current);
  }, []);

  /**
   * function to register listeners
   */
  const registerListeners = useCallback(
    function listeners() {
      /** event listeners for the custom plugins */
      let blots = [];
      editor.current.scroll.emitter.on(Scroll.events.BLOT_MOUNT, (blot) => {
        blots.push(blot);
        if (blots.length > 0) {
          onBlotMounted(...blots);
          blots = [];
        }
      });
      editor.current.scroll.emitter.on(
        Scroll.events.BLOT_UNMOUNT,
        onBlotUnmounted
      );
    },
    [onBlotMounted, onBlotUnmounted]
  );

  const handleTextChange = useCallback(
    function textChangeEvent(newDelta, oldDelta) {
      setTimeout(() => {
        const range = editor.current.getSelection();
        if (!range) return;
        if (
          slashCommandTarget.showTargetRenderer &&
          [FORMAT_TYPES.VIDEO, FORMAT_TYPES.LINK].includes(
            slashCommandTarget.attachmentType
          ) &&
          editor.current.restrictPasteHandler
        ) {
          let pastedText = editor.current.pastedText || "";
          pastedText = pastedText.trim();
          if (isUrl(pastedText)) {
            if (slashCommandTarget.attachmentType === FORMAT_TYPES.LINK) {
              if (!pastedText.match(PROTOCOL_REGEX)) {
                pastedText = `https://${pastedText}`;
              }
              const linkObj = {
                href: pastedText,
                text: pastedText
              };
              const range = editor.current.getSelection(true);
              if (!range.length) {
                const text = pastedText || "";
                editor.current.updateContents(
                  new Delta()
                    .retain(range.index - text.length)
                    .retain(text.length, { link: linkObj })
                );
              }
            } else if (
              slashCommandTarget.attachmentType === FORMAT_TYPES.VIDEO
            ) {
              editor.current.updateContents(
                new Delta()
                  .retain(
                    editor.current.getSelection(true).index - pastedText.length
                  )
                  .delete(pastedText.length)
              );
              const { blot } = insertBlock(FORMAT_TYPES.VIDEO, {
                url: pastedText
              });
              const blotIndex = editor.current.getIndex(blot);
              range.index = blotIndex;
              range.length = 1;
            }
            editor.current.restrictPasteHandler = false;
            editor.current.pastedText = "";
          } else {
            editor.current.restrictPasteHandler = false;
            editor.current.pastedText = "";
          }
        }
        if (
          slashCommandTarget.showTargetRenderer &&
          slashCommandTarget.attachmentType === FORMAT_TYPES.IMAGE &&
          slashCommandTarget.currentIndex !== range.index
        ) {
          resetSlashCommandTarget();
        }
        let top = 0;
        const [line] = editor.current.getLine(range.index);
        const domTag = line && line.domNode.tagName;
        const result = getTagNameByLine(line, domTag);
        if (["blockquote", "ul", "ol", "pre"].includes(result)) {
          const index = editor.current.getIndex(line.parent);
          const { top: yAxis } = editor.current.getBounds(index);
          top = yAxis - 2;
        } else if (["h1", "h2", "p"].includes(result)) {
          const index = editor.current.getIndex(line);
          const { top: yAxis } = editor.current.getBounds(index);
          top = yAxis - 2;
        }
        setEditorState((state) => {
          return {
            ...state,
            sideToolbar: {
              top,
              result
            }
          };
        });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editorState]
  );

  useEffect(
    function slashPlaceholderCleanup() {
      if (readOnly && editor.current) {
        clearPrevLinePlaceholder(editor.current);
        resetSlashCommandTarget();
        editor.current.restrictPasteHandler = false;
        editor.current.pastedText = "";
        pluginRendererRef.current = false;
        setEditingLink(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [readOnly]
  );

  const handleSelectionChange = useCallback(
    function selectionChangeEvent() {
      clearPrevLinePlaceholder(editor.current);
      if (slashCommandTarget.showTargetRenderer && !pluginRendererRef.current) {
        const range = editor.current.getSelection(true);
        if (slashCommandTarget.currentIndex !== range.index) {
          resetSlashCommandTarget();
        }
      }
      setTimeout(() => {
        const range = editor.current.getSelection();
        if (!range) return;
        let top = 0;
        const [line] = editor.current.getLine(range.index);
        const domTag = line && line.domNode.tagName;
        if (isCurrentLineEmpty(line, editor.current) && range.length === 0) {
          line.domNode.dataset.placeholder = `Enter '/' for commands`;
        }
        const result = getTagNameByLine(line, domTag);
        if (["blockquote", "ul", "ol", "pre"].includes(result)) {
          const index = editor.current.getIndex(line.parent);
          const { top: yAxis } = editor.current.getBounds(index);
          top = yAxis - 2;
        } else if (["h1", "h2", "p"].includes(result)) {
          const index = editor.current.getIndex(line);
          const { top: yAxis } = editor.current.getBounds(index);
          top = yAxis - 2;
        }
        if (range && range.length > 0) {
          const bounds = editor.current.getBounds(range);
          let inlineToolbarBounds = { ...bounds };

          const editorContainerElement = editorContainer.current;

          if (editorContainerElement) {
            inlineToolbarBounds.left =
              inlineToolbarBounds.left + editorContainerElement.offsetLeft;
          }

          // Finding if editor scrolls within scrollContainer,
          // To show inlineToolbar at top of editor
          const scrollContainerElement = editor.current?.scrollingContainer;

          if (
            scrollContainerElement &&
            editorContainerElement &&
            scrollContainerElement.scrollHeight <=
              editorContainerElement.scrollHeight &&
            scrollContainerElement.scrollTop > bounds.top
          ) {
            inlineToolbarBounds.height =
              inlineToolbarBounds.height -
              (scrollContainerElement.scrollTop - bounds.top);
            inlineToolbarBounds.top = scrollContainerElement.scrollTop;
          }

          setEditorState((state) => {
            return {
              ...state,
              inlineToolbar: {
                ...state.inlineToolbar,
                width: inlineToolbarBounds.width,
                height: inlineToolbarBounds.height,
                top: inlineToolbarBounds.top,
                left: inlineToolbarBounds.left,
                showToolbar: true
              },
              sideToolbar: {
                top,
                result
              }
            };
          });
        } else if (range) {
          setEditorState((state) => {
            return {
              ...state,
              inlineToolbar: {
                ...state.inlineToolbar,
                showToolbar: false
              },
              sideToolbar: {
                top,
                result
              }
            };
          });
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editorState]
  );

  const onEditorChange = useCallback(
    function onEditorChange(type, newValue, _oldValue, source) {
      if (["selection-change", "text-change"].includes(type)) {
        if (type === "selection-change" && newValue) {
          handleSelectionChange(newValue);
        }
        if (type === "text-change") {
          onChange && onChange();
          handleTextChange(newValue, _oldValue);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSelectionChange, handleTextChange, onChange, slashCommandTarget]
  );

  function enterHandler() {
    if (markdown && (!isEmpty() || !shouldCheckEmptyonEnter)) {
      onEnter(markdownFromDelta(editor.current.getContents()));
    } else if (!markdown && (!isEmpty() || !shouldCheckEmptyonEnter)) {
      onEnter(
        isHTML
          ? deserializeHTML(editor.current.getContents())
          : deserializer(editor.current.getContents())
      );
    }
    if (clearOnEnter) editor.current.setContents([]);
    return false;
  }

  const insertBlock = useCallback(function insertBlockHandler(
    type,
    data,
    config = {}
  ) {
    const {
      newLine = true,
      spaceRequired = false,
      removeTypedText = false
    } = config;
    const range = editor.current.getSelection(true);
    if (!range) return null;
    let blot = null;
    /** To remove the entered text and replace with mention blot */
    if (removeTypedText) {
      const index = range.index - data.text.length;
      editor.current.updateContents(
        new Delta().retain(index).delete(data.text.length)
      );
      let spaceChar = "";
      if (spaceRequired) {
        spaceChar = " ";
      }
      editor.current.updateContents(
        new Delta()
          .retain(index)
          .insert({
            [type]: data[type]
          })
          .insert(spaceChar)
      );
      if (newLine) editor.current.setSelection(index + 2);
      else editor.current.setSelection(range.index + 1);
    } else {
      editor.current.insertEmbed(range.index, type, data);
      if (spaceRequired) editor.current.insertText(range.index + 1, " \u200b");
      if (newLine) editor.current.setSelection(range.index + 2);
      else editor.current.setSelection(range.index + 1);
    }
    [blot] = editor.current.getLeaf(range.index);
    if (blot?.statics.name !== "CustomBlock") {
      [blot] = editor.current.getLeaf(range.index + 1);
    }
    const removeBlock = () => {
      const blotIndex = editor.current.getIndex(blot);
      blotIndex !== -1 && editor.current.deleteText(blotIndex, 1);
      return blotIndex;
    };
    return { blot, removeBlock };
  },
  []);

  const insertEmbedAtBlotIndex = useCallback(
    function insertEmbedAtBlotIndexHandler(blotIndex, type, data) {
      editor.current.insertEmbed(blotIndex, type, data);
      editor.current.enable();
      editor.current.focus();
    },
    []
  );

  function isEmpty() {
    if ((editor.current.getContents()["ops"] || []).length !== 1) {
      return false;
    }
    return editor.current.getText().trim().length === 0;
  }

  useEffect(function instantiateEditor() {
    function hyperLinkHandler(e) {
      if (e?.target?.nodeName === "A" && readOnly) {
        try {
          const { href = "" } = e.target;
          const linkHref = new URL(href);
          if (linkHref?.host === window.location.host) {
            history.push(linkHref.pathname.replace("/view", ""));
            e.preventDefault();
          }
          return;
        } catch (e) {
          console.info("error while clicking hyper link", e);
        }
      }
    }
    currentValue.current = cloneDeep(value);
    /** Register formats for the editor */
    plugins.forEach((item) => {
      let pluginComponent = item.component;
      if (CUSTOM_TYPES.includes(item.formatType)) {
        pluginComponent = CustomBlock(item.formatType, item.component, flowId);
      }
      if (item.formatType === FORMAT_TYPES.BLOCKQUOTE) {
        formats.push("block_quote/blockquote_item");
      } else if (item.formatType === FORMAT_TYPES.LIST) {
        formats.push("list/list-item");
      }
      formats.push(`${item.formatType}`);
      Quill.register(`formats/${item.formatType}`, pluginComponent, true);
    });

    /** Editor instantiation */
    editor.current = new Quill(editorContainer.current, {
      placeholder,
      readOnly,
      formats,
      onFilePaste: copyPasteConfig.onFilePaste,
      imageConfig: copyPasteConfig.imageConfig,
      scrollingContainer:
        parentContainer || getScrollParent(editorContainer.current)
    });
    editor.current.restrictPasteHandler = false;
    if (flowType && flowId) {
      editor.current.flowType = flowType;
      editor.current.flowId = flowId;
    }

    setReadonly(readOnly);
    registerListeners();

    /** Painting the editor with value */
    if (markdown) {
      editor.current.setContents(
        editor.current.clipboard.convert(htmlMarkup(currentValue.current))
      );
    } else {
      const serializeOps = isHTML
        ? serializeHTML(currentValue.current, readOnly)
        : serializer(currentValue.current, readOnly);
      const delta = new Delta(serializeOps);
      editorReference.current = delta;
      editor.current.setContents(delta);
    }

    if (disableTab && editor.current?.getModule("keyboard")) {
      Reflect.deleteProperty(
        editor.current.getModule("keyboard").bindings,
        "9"
      );
    }

    /** keyboard binding for the editor to detect links on typing */
    editor.current.keyboard.addBinding({
      collapsed: true,
      key: " ",
      prefix: /https?:\/\/[^\s]+/,
      handler: function handler(range, context) {
        const regex = /https?:\/\/[^\s]+/;
        const prefixMatch = context.prefix.match(regex);
        if (prefixMatch === null) return true;
        const prefixLength = prefixMatch[0].length;
        const prefixStart = range.index - prefixLength;
        const url = editor.current.getText(prefixStart, prefixLength);
        editor.current.formatText(
          prefixStart,
          prefixLength,
          { link: { href: url, text: url } },
          "user"
        );
        return true;
      }
    });

    /** Focusing the editor */
    focusEditor(focus);

    /** Register editor change listener only if the editor any toolbar */

    editor.current.root.classList += ` ${className}`;
    editor.current.history.clear();

    editor.current?.root?.addEventListener("click", hyperLinkHandler);

    const debouncedFunc = debounce(handleChange, 100);

    function editorChangeHandler() {
      debouncedFunc();
    }

    function handleChange() {
      if (!onSelectionChange) return;
      const range = editor.current.getSelection();
      if (!range?.index) return;
      const formats = editor.current.getFormat(range);
      const rangeText = editor.current.getText(range);
      const selectedFormats = {
        ...formats,
        [ASSIST_FORMAT_TYPES[formats.list || formats.header]]: true
      };
      onSelectionChange({
        selectedFormats,
        rangeText
      });
    }

    if (onSelectionChange) {
      editor.current.on("editor-change", editorChangeHandler);
    }

    setFocus(focus);
    return () => {
      editor.current?.root?.removeEventListener("click", hyperLinkHandler);
      if (onSelectionChange) {
        editor.current.off("editor-change", editorChangeHandler);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    function quillEditorChangeEvent() {
      if ((inlineToolbar || sideToolbar) && !readOnly) {
        editor.current.on("editor-change", onEditorChange);
      }
      return () => {
        if ((inlineToolbar || sideToolbar) && !readOnly) {
          editor.current.off("editor-change", onEditorChange);
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [readOnly, onEditorChange]
  );

  /** Registering listeners */
  useEffect(
    function handlingEditorEvents() {
      function handleOnblur(e) {
        if (
          !(
            e.relatedTarget &&
            (e.relatedTarget.classList.contains("dynamicinput") ||
              e.relatedTarget.offsetParent?.classList.contains(
                "dynamicinput"
              ) ||
              e.relatedTarget.classList.contains("toolbar") ||
              e.relatedTarget.classList.contains("mentioncontainer") ||
              e.relatedTarget.classList.contains("ql-clipboard"))
          )
        ) {
          setEditorFocussed(false);
          const isToolbarRenderer =
            e.relatedTarget?.offsetParent?.classList.contains("toolbar-option");
          const isPluginRendererOpen = pluginRendererRef.current;
          if (!isToolbarRenderer && !isPluginRendererOpen) {
            setEditorState((state) => {
              return {
                ...state,
                inlineToolbar: {
                  ...state.inlineToolbar,
                  showToolbar: false
                }
              };
            });
          }
          if (onBlur && !isToolbarRenderer && !isPluginRendererOpen) {
            const delta = editor.current.getContents();
            if (markdown) {
              onBlur(markdownFromDelta(delta));
            } else if (isHTML) {
              onBlur(deserializeHTML(delta));
            } else {
              onBlur(deserializer(delta));
            }
          }

          if (
            slashCommandTarget.showTargetRenderer &&
            !pluginRendererRef.current
          ) {
            resetSlashCommandTarget();
          }
          clearPrevLinePlaceholder(editor.current);
        }
      }

      function onClickHandler(e) {
        onClick && onClick(e);
      }

      function handleFocus() {
        onFocus && onFocus();
        setEditorFocussed(true);
      }

      editor.current.root.addEventListener("click", onClickHandler);
      editor.current.root.addEventListener("blur", handleOnblur);
      editor.current.root.addEventListener("focus", handleFocus);

      return () => {
        editor.current.root.removeEventListener("click", onClickHandler);
        editor.current.root.removeEventListener("blur", handleOnblur);
        editor.current.root.removeEventListener("focus", handleFocus);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClick, onChange, onBlur, markdown, editorState]
  );

  useEffect(
    function onChangeHandler() {
      function onChangeHandler(delta, oldDelta, source) {
        if (source !== "user") return;
        const content = editor.current.getContents();
        if (markdown) {
          onChange(markdownFromDelta(content));
        } else {
          const serializeContent = isHTML
            ? deserializeHTML(content)
            : deserializer(content);
          onChange(serializeContent);
        }
      }
      if (onChange && !(inlineToolbar || sideToolbar)) {
        editor.current.on("text-change", onChangeHandler);
      }
      return () => {
        if (onChange && !(inlineToolbar || sideToolbar)) {
          editor.current.off("text-change", onChangeHandler);
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange]
  );

  useEffect(
    function focusQuillEditor() {
      if (editor.current) {
        if (currentFocus !== focus) {
          focusEditor(focus);
          setFocus(focus);
        }
      }
    },
    [currentFocus, focus, focusEditor, readOnly]
  );

  useEffect(
    function searchTextInEditor() {
      /**
       * to find the keyword inside the editor - eg.ChatSearch
       */
      const delta = new Delta(editorReference.current);
      if (editor.current) {
        const newDelta = [];
        if (typeof searchText === "string" && searchText) {
          delta.ops.forEach((item) => {
            if (typeof item.insert !== "string") {
              newDelta.push(item);
              return;
            }
            const isMatch = item.insert
              .toLowerCase()
              .match(searchText.toLowerCase());
            if (isMatch) {
              const obj1 = {
                attributes: item.attributes || {},
                insert: item.insert.slice(0, isMatch.index)
              };
              const obj2 = {
                attributes: {
                  ...item.attributes,
                  background: "var(--kf-color-secondary-three-200)",
                  color: "var(--kf-color-gray-900)"
                },
                insert: item.insert.slice(
                  isMatch.index,
                  isMatch.index + searchText.length
                )
              };
              const obj3 = {
                attributes: item.attributes || {},
                insert: item.insert.slice(
                  isMatch.index + searchText.length,
                  item.insert.length
                )
              };
              newDelta.push(obj1);
              newDelta.push(obj2);
              newDelta.push(obj3);
            } else {
              newDelta.push(item);
            }
          });
          editor.current.setContents(new Delta(newDelta));
        } else if (markdown) {
          editor.current.setContents(
            editor.current.clipboard.convert(htmlMarkup(currentValue.current))
          );
        } else if (isHTML) {
          const delta = new Delta(
            serializeHTML(currentValue.current, readOnly)
          );
          editor.current.setContents(delta);
        } else {
          const delta = new Delta(serializer(currentValue.current, readOnly));
          editor.current.setContents(delta);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchText]
  );

  useEffect(
    function editorContentParser() {
      if (
        markdown &&
        markdownFromDelta(editor.current.getContents()) !== value
      ) {
        /** to prevent focussing the editor */
        editor.current.disable();
        editor.current.setContents(
          editor.current.clipboard.convert(htmlMarkup(value))
        );
        if (!readOnly) {
          setTimeout(() => {
            editor.current.enable();
          });
        }
      } else if (
        // !isEqual(currentValue.current, value) &&
        !markdown &&
        !searchText
      ) {
        const delta = new Delta(
          isHTML ? serializeHTML(value, readOnly) : serializer(value, readOnly)
        );
        editor.current.setContents(delta);
        currentValue.current = cloneDeep(value);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markdown, value]
  );

  useEffect(
    function updateReadonlyStatus() {
      if (currentReadonly !== readOnly) {
        if (!readOnly) editor.current.enable();
        else editor.current.disable();
        setReadonly(readOnly);
      }
    },
    [currentReadonly, readOnly]
  );

  useEffect(
    function onEnterBinding() {
      /** Editor content will be returned on Enter if onEnter props is passed */
      if (onEnter && !isPopupOpen) {
        if (shiftReturn) {
          Reflect.deleteProperty(editor.current.keyboard.bindings, 13);
          editor.current.keyboard.bindings[13] = [];
        }
        editor.current.keyboard.bindings[13].splice(0, 1);
        let handleEnter = {
          key: 13,
          shiftKey: false,
          customEnter: true,
          handler: enterHandler
        };
        editor.current.keyboard.bindings[13].unshift(handleEnter);
        if (shiftReturn) {
          editor.current.keyboard.bindings[13].unshift({
            key: 13,
            shiftKey: true,
            handler: () => null
          });
        }
      } else {
        const enterIndex = editor.current.keyboard.bindings[13].findIndex(
          (key) => key.customEnter
        );
        if (enterIndex > -1) {
          editor.current.keyboard.bindings[13].splice(enterIndex, 1);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [markdown, onEnter, isPopupOpen]
  );

  useEffect(
    function handleOnScroll() {
      if (
        editorState.inlineToolbar.showToolbar ||
        slashCommandTarget.showTargetRenderer
      ) {
        let overlayElem = window;
        if (overlayElem) {
          let { listen, unListen } = MouseWheelListen(
            overlayElem,
            (dx, dy, dz, evt) => {
              if (dy > 3 || dy < -3) {
                let link = document.getElementById("editorLink");
                if (link) link.style.display = "none";
                setEditorState((state) => {
                  return {
                    ...state,
                    inlineToolbar: {
                      ...state.inlineToolbar,
                      showToolbar: false
                    }
                  };
                });
              }
            }
          );
          listen();
          return () => {
            unListen();
          };
        }
      }
    },
    [
      editorState.inlineToolbar.showToolbar,
      slashCommandTarget.showTargetRenderer
    ]
  );

  useEffect(
    function linkPasteHandler() {
      if (onLinkPaste && editor.current) {
        editor.current.onLinkPaste = onLinkPaste;
      }
    },
    [onLinkPaste]
  );

  useImperativeHandle(innerRef, () => {
    /** Editor functions - start */
    function insertEmoji(emoji) {
      const range = editor.current.getSelection(true);
      if (!range) return null;
      editor.current.insertText(range.index, emoji);
      editor.current.setSelection(range.index + emoji.length);
    }

    function getContents() {
      const delta = editor.current.getContents();
      if (markdown) {
        return markdownFromDelta(delta);
      } else if (isHTML) {
        return deserializeHTML(delta);
      } else {
        return deserializer(delta);
      }
    }

    // pass array of format types as parameter
    function getContentsByFormatType(formatTypes) {
      let delta = editor.current.getContents();
      let formats = {};
      delta &&
        delta.ops &&
        delta.ops.length > 0 &&
        delta.ops.forEach((item) => {
          formatTypes.forEach((formatType) => {
            let data = item.insert[formatType];
            if (typeof item.insert === "object" && data) {
              let id = item.insert[formatType].Id;
              formats[formatType] = { ...formats[formatType], [id]: data };
            }
          });
        });
      return formatTypes.length === 1 ? formats[formatTypes[0]] || {} : formats;
    }

    function clearContent() {
      editor.current.setContents("");
    }

    function getText() {
      return editor.current.getText();
    }

    function getInstance() {
      return editor.current;
    }

    function focus() {
      setTimeout(() => {
        const length = editor.current.getLength();
        editor.current.setSelection(length);
      });
    }

    function formatText(type, value) {
      let formatType = type;
      let formatValue = value;
      if (type === FORMAT_TYPES.ERASE) {
        editor.current.format(FORMAT_TYPES.CODE, false);
        editor.current.removeFormat(editor.current.getSelection(true));
        return;
      }
      if (type === FORMAT_TYPES.LINK) {
        const range = editor.current.getSelection(true);
        const text = value.text || value.href || "";
        editor.current.deleteText(range, text.length);
        editor.current.insertText(range, text);
        editor.current.setSelection(
          editor.current.getSelection(true).index - text.length,
          text.length
        );
      }
      if (ASSIST_FORMAT_TYPES[type]) {
        formatType = ASSIST_FORMAT_TYPES[type];
      }
      editor.current.format(formatType, formatValue);
    }

    function getFormat() {
      const range = editor.current.getSelection(true);
      return editor.current.getFormat(range);
    }

    function updatePlaceholder(text) {
      editor.current.root.dataset.placeholder = text;
    }

    return {
      insertBlock,
      insertEmoji,
      getContents,
      getContentsByFormatType,
      isEmpty,
      clearContent,
      getText,
      getInstance,
      focus,
      formatText,
      getFormat,
      updatePlaceholder,
      insertEmbedAtBlotIndex
    };
  });

  const blotComponents = useMemo(
    function blotRendering() {
      return Object.values(embedBlots).map((blot) => {
        const {
          statics: { blotName }
        } = blot;
        if (blotName && blot.id === newBlotId.current) {
          const plugin = plugins.find((item) => item.formatType === blotName);
          return blot.renderPortal(blot.id, plugin.component || null);
        }
        return blot.renderPortal(blot.id);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [embedBlots]
  );

  const validSlashCommandList = useMemo(
    function getValidPluginList() {
      return constructSlashCommandList(plugins);
    },
    [plugins]
  );

  const updatedAcceptedKeyStrokes = useMemo(
    function updatedAcceptedKeyStrokes() {
      let result = { ...acceptedKeyStrokes };
      if (allowSlashCommand) {
        result["/"] = (prop) => {
          const range = editor.current.getSelection();
          if (range) {
            const formats = editor.current?.getFormat(range);
            if (formats[FORMAT_TYPES.CODE]) {
              return null;
            }
          }
          return (
            <SlashCommandRenderer
              {...prop}
              slashCommands={validSlashCommandList}
              editor={editor.current}
            />
          );
        };
      }
      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [acceptedKeyStrokes]
  );

  function onSlashCommandSelect(selectedPlugin, data) {
    const { formatType } = selectedPlugin;
    const range = editor.current.getSelection(true);
    if (range) {
      const index = range.index - data.length;
      editor.current.updateContents(
        new Delta().retain(index).delete(data.length)
      );
      if (
        formatType === FORMAT_TYPES.FIELD ||
        formatType === FORMAT_TYPES.PROPERTY
      ) {
        // show insert field modal
        const bounds = editor.current.getBounds(index);

        setSlashCommandTarget((state) => ({
          ...state,
          showTargetRenderer: true,
          top: bounds.top,
          left: bounds.left + editorContainer.current.offsetLeft,
          height: bounds.height,
          attachmentType: formatType,
          selectedPlugin
        }));
        editor.current.restrictPasteHandler = false;
        editor.current.pastedText = "";
      } else if (
        [FORMAT_TYPES.IMAGE, FORMAT_TYPES.VIDEO, FORMAT_TYPES.LINK].includes(
          formatType
        )
      ) {
        const bounds = editor.current.getBounds(index);

        setSlashCommandTarget((state) => ({
          ...state,
          showTargetRenderer: true,
          top: bounds.top,
          left: bounds.left + editorContainer.current.offsetLeft,
          height: bounds.height,
          attachmentType: formatType,
          currentIndex: index,
          selectedPlugin
        }));
        if ([FORMAT_TYPES.LINK, FORMAT_TYPES.VIDEO].includes(formatType)) {
          editor.current.restrictPasteHandler = true;
        } else {
          editor.current.restrictPasteHandler = false;
          editor.current.pastedText = "";
        }
      } else {
        if (
          [
            FORMAT_TYPES.H1,
            FORMAT_TYPES.H2,
            FORMAT_TYPES.H3,
            FORMAT_TYPES.PARAGRAPH,
            FORMAT_TYPES.CODE,
            FORMAT_TYPES.BLOCKQUOTE
          ].includes(formatType)
        ) {
          if (formatType === FORMAT_TYPES.PARAGRAPH) {
            editor.current.format(FORMAT_TYPES.HEADER, false);
            editor.current.format(FORMAT_TYPES.CODE, false);
            editor.current.format(FORMAT_TYPES.BLOCKQUOTE, false);
          } else if (
            formatType === FORMAT_TYPES.CODE ||
            formatType === FORMAT_TYPES.BLOCKQUOTE
          ) {
            editor.current.format(formatType, true);
          } else {
            editor.current.format(
              FORMAT_TYPES.HEADER,
              ASSIST_FORMAT_TYPES[formatType]
            );
          }
        } else if (
          [FORMAT_TYPES.UL_LIST, FORMAT_TYPES.OL_LIST].includes(formatType)
        ) {
          editor.current.format(
            FORMAT_TYPES.LIST,
            ASSIST_FORMAT_TYPES[formatType]
          );
        } else {
          editor.current.format(formatType, true);
        }
        editor.current.restrictPasteHandler = false;
        editor.current.pastedText = "";
      }
    }
  }

  const resetSlashCommandTarget = useCallback(function resetSlashTargetState() {
    setSlashCommandTarget({
      left: 0,
      top: 0,
      showTargetRenderer: false,
      height: 0,
      attachmentType: null,
      currentIndex: null,
      selectedPlugin: null
    });
    pluginRendererRef.current = false;

    setEditingLink(false);
  }, []);

  const validInlineToolbarGroup = useMemo(
    function getInlineList() {
      return constructInlineToolbarList(plugins);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <RichtextContext.Provider
      value={{ editingLink, setEditingLink, editor: editor.current }}
    >
      <div
        className={`richtextContainer ${styles.editorContainer} ${
          sideToolbar ? styles.sideToolbarContainer : ""
        } ${containerClassname} ph-no-capture`}
        data-ls-disabled
        data-openreplay-obscured
        data-readonly={readOnly}
        translate="no"
        style={{
          "--file-picker-target-left": `${slashCommandTarget.left}px`,
          "--file-picker-target-top": `${slashCommandTarget.top}px`,
          "--file-picker-target-height": `${slashCommandTarget.height}px`
        }}
      >
        {updatedAcceptedKeyStrokes && (
          <SuggestionList
            editorRef={editor}
            insertBlock={insertBlock}
            acceptedKeyStrokes={updatedAcceptedKeyStrokes}
            onEnterHandler={enterHandler}
            onOpen={() => setIsPopupOpen(true)}
            onClose={() => setIsPopupOpen(false)}
            isEditorFocussed={editorFocussed}
            onSlashCommandSelect={onSlashCommandSelect}
            allowSlashCommand={allowSlashCommand}
          />
        )}
        {inlineToolbar && !readOnly && (
          <InlineToolbar
            toolbarRef={editor}
            editorState={editorState}
            allowSlashCommand={allowSlashCommand}
            validInlineToolbarGroup={validInlineToolbarGroup}
          />
        )}
        {!readOnly && (
          <>
            <AttachmentTooltip attachmentTooltip={slashCommandTarget} />
            <LinkCursorSelectionRenderer editor={editor.current} />
            {slashCommandTarget.showTargetRenderer &&
              slashCommandTarget.selectedPlugin?.renderer && (
                <PluginRenderer
                  insertBlock={insertBlock}
                  slashTargetStyles={slashCommandTarget}
                  pluginRendererRef={pluginRendererRef}
                  resetSlashCommandTarget={resetSlashCommandTarget}
                />
              )}
          </>
        )}
        {/* showDefaultFilePicker prop will be used to not mount default File Picker when custom Image plugin passed with its own File Picker renderer. Eg: Inside App - page rich text widget */}
        {showDefaultFilePicker && (
          <RichtextFilePicker
            filePrefix={flowId}
            slashTargetStyles={slashCommandTarget}
            insertBlock={insertBlock}
            fileOptions={fileOptions}
            pluginRendererRef={pluginRendererRef}
            insertEmbedAtBlotIndex={insertEmbedAtBlotIndex}
          />
        )}
        <div
          spellCheck={true}
          className={`${styles.editor} base`}
          ref={editorContainer}
        >
          {blotComponents}
        </div>
        {/* To mount tooltip container for link hover  */}
        <div className="linkTooltipWrapper" />
        {/* To make parent to take full height when children is float */}
        <p style={{ clear: "both" }} />
      </div>
    </RichtextContext.Provider>
  );
}

/**
 * An editor which support rich and custom formatting, if no props is passed a plain content-editable div will be rendered
 */
export const RichTextEditor = forwardRef((props, ref) => (
  <ErrorBoundary>
    <RichTextEditorComponent {...props} innerRef={ref} />
  </ErrorBoundary>
));

RichTextEditor.propTypes = {
  /** Class string which can be used to customise the editor */
  className: PropTypes.string,
  /** Input value to be passed to the editor, if its `markdown=true` it should be a String else array */
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  /** Wide variety of plugins to enable formatting, you need to pass it as an array for the required plugins. By default if no plugins is passed it will be an content-editable `div` */
  plugins: PropTypes.arrayOf(
    PropTypes.shape({
      formatType: PropTypes.string.isRequired,
      component: PropTypes.func,
      hideInSlashCommand: PropTypes.bool
    })
  ).isRequired,
  /** Empty placeholder for an editor */
  placeholder: PropTypes.string,
  /** If its `true` no actions can be performed. <b>[Deprecated]</b> Use Readonly component instead. */
  readOnly: PropTypes.bool,
  /** It controls the focus of the editor */
  focus: PropTypes.bool,
  /** Callback function to be executed on focus of the editor */
  onFocus: PropTypes.func,
  /** <b>[Deprecated]</b> Its enables a side toolbar which were used in Channel Post */
  sideToolbar: PropTypes.bool,
  /** If its enabled an inline toolbar will be displayed when the text selection happens */
  inlineToolbar: PropTypes.bool,
  /** Flowid need to be passed for handling attachments using filepicker */
  flowId: PropTypes.string,
  /** If a text is passed, it will highlight the matched content */
  searchText: PropTypes.string,
  /** If its true, it's behaves as an Markdown editor, meaning the output will be an markdown string */
  markdown: PropTypes.bool,
  /** Its a custom handler which accepts an object, where key denotes an character if that character is typed inside the editor, the value will be an component and that will be rendered. Usually this helps in `@mention/#hashtag` */
  acceptedKeyStrokes: PropTypes.objectOf(PropTypes.func),
  /** If its true shiftReturn action can be performed, hence onEnter callback not be invoked */
  shiftReturn: PropTypes.bool,
  /** If its true when performing enter action the editor will be cleared */
  clearOnEnter: PropTypes.bool,
  /** If its true, onEnter callback will not be fired if the editor is empty */
  shouldCheckEmptyonEnter: PropTypes.bool,
  /** A ref to be passed to control the methods which is exposed */
  innerRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }).isRequired,
  /** If its true, performing tab action inside the editor is restricted */
  disableTab: PropTypes.bool,
  /** Type of the flow to be passed used for handling attachments inside Filepicker */
  flowType: PropTypes.string,
  /** It'll be true for form, if its true the formatting is handled through a slash command */
  allowSlashCommand: PropTypes.bool,
  /** Its a supporting config for handling the attachment for filepickerv2 */
  fileOptions: PropTypes.shape({
    fileExtensions: PropTypes.arrayOf(PropTypes.string).isRequired,
    imageProps: PropTypes.shape({
      sizes: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
      crop: PropTypes.bool.isRequired
    }).isRequired,
    maxCount: PropTypes.number.isRequired
  }),
  /** Classname string for the outer container */
  containerClassname: PropTypes.string,
  /** An callback will be fired when the text gets selected, it returns the object with formats which the text contains */
  onSelectionChange: PropTypes.func,
  /** An callback will be fired when pasting a link */
  onLinkPaste: PropTypes.func,
  /** An callback will be fired when enter is pressed */
  onEnter: PropTypes.func,
  /** An callback will be fired when anything changes in the editor */
  onChange: PropTypes.func,
  /** An callback will be fired during onBlur */
  onBlur: PropTypes.func,
  /** An callback will be fired when click is performed on the editor */
  onClick: PropTypes.func,
  /** If its true serializehtml else serialize action can be performed */
  isHTML: PropTypes.bool,
  /** This will have the reference of the parent container */
  parentContainer: PropTypes.element,
  /** Its a supporting config for handling File copy paste */
  copyPasteConfig: PropTypes.shape({
    onFilePaste: PropTypes.func,
    imageConfig: PropTypes.shape({
      imageProps: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))
    })
  }),
  /** This will allow user to enable default platform's file picker options */
  showDefaultFilePicker: PropTypes.bool
};

RichTextEditor.defaultProps = {
  className: "",
  value: "",
  plugins: [],
  readOnly: false,
  searchText: "",
  markdown: false,
  inlineToolbar: true,
  sideToolbar: false,
  shiftReturn: false,
  clearOnEnter: true,
  shouldCheckEmptyonEnter: true,
  disableTab: false,
  allowSlashCommand: true,
  containerClassname: ""
};

RichTextEditorComponent.propTypes = RichTextEditor.propTypes;

RichTextEditorComponent.defaultProps = RichTextEditor.defaultProps;
